import { ErrorResponse } from "@/app/types/error";
import {
  LoginDTO,
  LoginProviderResponse,
  ManualRegisterResponse,
  RegisterManualDTO,
  ResetPasswordDTO,
  UpdatePasswordDTO,
  ValidateCodeDTO,
  BasicAuthResponse,
  ValidateMFAResponse,
  ToggleMFAResponse,
  SendMFAResponse,
} from "./types";
import { patchApi, postApi } from "@/app/api/config";
import { getItemSessionStorage } from "@/lib/utils";
import { ELocalStorageKeys } from "@/app/types/localStorage";

export const loginProvider = async (
  data: LoginDTO,
): Promise<LoginProviderResponse | ErrorResponse> => {
  const response = await postApi<LoginProviderResponse | ErrorResponse>(
    "/auth/provider/login",
    {
      body: data,
    },
  );

  if ("status" in response && response.status === "fail") {
    return response as ErrorResponse;
  }
  return response;
};

export const registerManualProvider = async (
  data: RegisterManualDTO,
): Promise<ManualRegisterResponse | ErrorResponse> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await postApi<ManualRegisterResponse | ErrorResponse>(
    "/auth/manual/provider/register",
    {
      body: data,
    },
    token,
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const sendProviderPasswordCode = async (
  email: string,
): Promise<BasicAuthResponse | ErrorResponse> => {
  const response = await postApi<BasicAuthResponse | ErrorResponse>(
    "/auth/provider/password/code",
    {
      body: { email },
    },
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const validateProviderPasswordCode = async (
  data: ValidateCodeDTO,
): Promise<BasicAuthResponse | ErrorResponse> => {
  const response = await postApi<BasicAuthResponse | ErrorResponse>(
    "/auth/provider/password/validate-email",
    {
      body: { email: data.email, code: data.code },
    },
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const resetProviderPassword = async (
  data: ResetPasswordDTO,
): Promise<BasicAuthResponse | ErrorResponse> => {
  const { email, confirmNewPassword, newPassword } = data;

  const response = await patchApi<BasicAuthResponse | ErrorResponse>(
    "/auth/provider/forgot-password",
    {
      body: {
        email,
        newPassword,
        confirmNewPassword,
      },
    },
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const updateProviderPassword = async (
  data: UpdatePasswordDTO,
): Promise<BasicAuthResponse | ErrorResponse> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await patchApi<BasicAuthResponse | ErrorResponse>(
    "/auth/provider/update-password",
    {
      body: {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
        confirmNewPassword: data.confirmNewPassword,
      },
    },
    token,
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const validateMFACode = async (
  data: ValidateCodeDTO,
): Promise<ValidateMFAResponse | ErrorResponse> => {
  const { email, code } = data;

  const response = await postApi<ValidateMFAResponse | ErrorResponse>(
    "/auth/provider/mfa/verify",
    {
      body: {
        email,
        code,
      },
    },
  );
  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const resendMFACode = async (
  email: string,
): Promise<SendMFAResponse | ErrorResponse> => {
  const response = await postApi<SendMFAResponse | ErrorResponse>(
    "/auth/provider/mfa/send-code",
    {
      body: {
        email: email,
      },
    },
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const changeMFAConfig = async (
  status: boolean,
): Promise<ToggleMFAResponse | ErrorResponse> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);
  const response = await patchApi<ToggleMFAResponse | ErrorResponse>(
    "/auth/provider/mfa/toggle",
    {
      body: { status },
    },
    token,
  );
  if (response.status === "fail") return response as ErrorResponse;
  return response;
};
